/**
 * @project E-School Web
 * @author Vothana CHY
 * @date 2026-07-13
 */

import { ref, onMounted, type Ref } from 'vue';
import { useEventListener } from '@vueuse/core';

export function useDragToScroll(
  containerRef: Ref<HTMLDivElement | null>,
  options?: {
    multiplier?: number;
    onDragStart?: () => void;
    onDragEnd?: () => void;
    onDragMove?: (scrollLeft: number) => void;
  }
) {
  const multiplier = options?.multiplier ?? 1.0; // Natural 1:1 scroll speed by default
  const isDragging = ref(false);
  let lastX = 0;
  let targetScrollLeft = 0;
  let lastScrollLeft = 0;
  let animationFrameId: number | null = null;

  const updateScroll = () => {
    if (!containerRef.value) return;

    const current = containerRef.value.scrollLeft;

    // Detect external teleportation (wrap-around) and adjust targetScrollLeft
    if (lastScrollLeft !== 0 && Math.abs(current - lastScrollLeft) > 150) {
      const offset = current - lastScrollLeft;
      targetScrollLeft += offset;
    }

    // Smoothly interpolate scroll position (lerp)
    const diff = targetScrollLeft - current;

    if (Math.abs(diff) < 0.5) {
      containerRef.value.scrollLeft = targetScrollLeft;
      lastScrollLeft = targetScrollLeft;
      animationFrameId = null;
    } else {
      const nextScroll = current + diff * 0.15; // Smooth 0.15 lerp factor
      containerRef.value.scrollLeft = nextScroll;
      lastScrollLeft = nextScroll;
      animationFrameId = requestAnimationFrame(updateScroll);
    }

    options?.onDragMove?.(containerRef.value.scrollLeft);
  };

  const handleMouseDown = (e: MouseEvent) => {
    if (!containerRef.value) return;
    isDragging.value = true;
    lastX = e.pageX;
    targetScrollLeft = containerRef.value.scrollLeft;
    lastScrollLeft = targetScrollLeft;

    containerRef.value.style.scrollBehavior = 'auto';
    containerRef.value.style.scrollSnapType = 'none';
    containerRef.value.style.cursor = 'grabbing';
    containerRef.value.style.userSelect = 'none';

    options?.onDragStart?.();
  };

  const handleMouseRelease = () => {
    if (!isDragging.value || !containerRef.value) return;
    isDragging.value = false;

    containerRef.value.style.scrollBehavior = 'smooth';
    containerRef.value.style.removeProperty('scroll-snap-type');
    containerRef.value.style.cursor = 'grab';
    containerRef.value.style.removeProperty('user-select');

    if (animationFrameId) {
      cancelAnimationFrame(animationFrameId);
      animationFrameId = null;
    }

    options?.onDragEnd?.();
  };

  const handleMouseMove = (e: MouseEvent) => {
    if (!isDragging.value || !containerRef.value) return;
    e.preventDefault();
    const deltaX = e.pageX - lastX;
    lastX = e.pageX;

    // Update target coordinate instead of instant scroll position change
    targetScrollLeft -= deltaX * multiplier;

    // Add slight elastic bounds (rubber banding) at edges
    const maxScroll = containerRef.value.scrollWidth - containerRef.value.clientWidth;
    targetScrollLeft = Math.max(-50, Math.min(maxScroll + 50, targetScrollLeft));

    if (!animationFrameId) {
      lastScrollLeft = containerRef.value.scrollLeft;
      animationFrameId = requestAnimationFrame(updateScroll);
    }
  };

  onMounted(() => {
    if (containerRef.value) {
      containerRef.value.style.cursor = 'grab';

      useEventListener(containerRef, 'mousedown', handleMouseDown);
      useEventListener(containerRef, 'mousemove', handleMouseMove);
      useEventListener(containerRef, 'mouseup', handleMouseRelease);
      useEventListener(containerRef, 'mouseleave', handleMouseRelease);
    }
  });

  return {
    isDragging,
  };
}
